import { OppButton } from '../ui/control';
import { Link } from 'react-router-dom';
import { Settings, Copy, CheckCheck } from 'lucide-react';

interface OppProfileHeroProps {
  bannerUrl?: string | null;
  avatarUrl?: string | null;
  displayName: string;
  bio?: string | null;
  initial: string;
  userLevel: number;
  levelProgress: number;
  opCode: string;
  copied: boolean;
  onCopyOpCode: () => void;
}

export function OppProfileHero({
  bannerUrl,
  avatarUrl,
  displayName,
  bio,
  initial,
  userLevel,
  levelProgress,
  opCode,
  copied,
  onCopyOpCode,
}: OppProfileHeroProps) {
  return (
    <div
      className="relative h-[var(--litopia-layout-220px)] w-full overflow-hidden bg-[hsl(var(--opp-hero))] md:h-[var(--litopia-layout-260px)]"
      style={{
        ...(bannerUrl
          ? { backgroundImage: `url(${bannerUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : undefined),
      }}
    >
      {bannerUrl && (
        <div className="absolute inset-0 bg-gradient-to-b from-scrim/20 via-transparent to-scrim/60" />
      )}

      <div className="absolute bottom-0 left-0 right-0 px-4 pb-6 md:px-8 lg:px-12">
        <div className="flex items-end gap-5">
          <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border-4 border-card bg-[hsl(var(--opp-accent-soft))] shadow-xl md:h-32 md:w-32">
            {avatarUrl ? (
              <img src={avatarUrl} alt={displayName} className="h-full w-full object-cover" />
            ) : (
              <span className="font-serif text-3xl font-bold text-[hsl(var(--opp-accent-strong))] md:text-4xl">
                {initial}
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0 pb-1">
            <div className="flex items-end justify-between gap-3">
              <div className="min-w-0">
                <h1 className={`truncate font-serif text-2xl font-bold md:text-3xl ${bannerUrl ? 'opp-profile-on-image drop-shadow-md' : 'text-[hsl(var(--opp-ink))]'}`}>
                  {displayName}
                </h1>
                <p className={`mt-0.5 text-sm ${bannerUrl ? 'opp-profile-on-image-muted' : 'text-[hsl(var(--opp-muted))]'}`}>
                  @{displayName.toLowerCase().replace(/\s+/g, '_')}
                </p>
                {bio && (
                  <p className={`mt-1.5 max-w-md line-clamp-2 text-sm ${bannerUrl ? 'opp-profile-on-image-subtle' : 'text-[hsl(var(--opp-accent-strong))]'}`}>
                    {bio}
                  </p>
                )}
                <div className="mt-2 flex items-center gap-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${bannerUrl ? 'bg-on-image/20 opp-profile-on-image' : 'bg-[hsl(var(--opp-accent-soft))] text-[hsl(var(--opp-accent-strong))]'}`}>
                    Lv{userLevel}
                  </span>
                  <div className="h-1.5 w-20 overflow-hidden rounded-full bg-on-image/30">
                    <div className="h-full rounded-full bg-[hsl(var(--opp-accent-soft))] transition-all" style={{ width: `${levelProgress}%` }} />
                  </div>
                </div>
              </div>

              <div className="hidden shrink-0 items-center gap-2 md:flex">
                <OppButton
                  onClick={onCopyOpCode}
                  className={`flex min-h-11 items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                    bannerUrl
                      ? 'border-card/40 bg-on-image/10 opp-profile-on-image hover:bg-on-image/20'
                      : 'border-[hsl(var(--opp-border))] text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-surface-raised))]'
                  }`}
                >
                  {copied ? <CheckCheck className="h-3.5 w-3.5 text-feedback-success" /> : <Copy className="h-3.5 w-3.5" />}
                  OP: {opCode}
                </OppButton>
                <Link
                  to="/settings"
                  className={`flex min-h-11 items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                    bannerUrl
                      ? 'border-card/40 bg-on-image/10 opp-profile-on-image hover:bg-on-image/20'
                      : 'border-[hsl(var(--opp-border))] text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-surface-raised))]'
                  }`}
                >
                  <Settings className="h-3.5 w-3.5" />
                  账户与设置
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 md:hidden">
          <OppButton
            onClick={onCopyOpCode}
            className={`flex min-h-11 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium ${
              bannerUrl
                ? 'border-card/40 bg-on-image/10 opp-profile-on-image'
                : 'border-[hsl(var(--opp-border))] text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-surface-raised))]'
            }`}
          >
            {copied ? <CheckCheck className="h-3.5 w-3.5 text-feedback-success" /> : <Copy className="h-3.5 w-3.5" />}
            OP: {opCode}
          </OppButton>
          <Link
            to="/settings"
            className={`flex min-h-11 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium ${
              bannerUrl
                ? 'border-card/40 bg-on-image/10 opp-profile-on-image'
                : 'border-[hsl(var(--opp-border))] text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-surface-raised))]'
            }`}
          >
            <Settings className="h-3.5 w-3.5" />
            账户与设置
          </Link>
        </div>
      </div>
    </div>
  );
}
